// Regras puras da BLACKFEO 2026. URLs administrativas legadas são preservadas.
import { valorLiquidoUnica } from "@/lib/black2x1/juros-parcelamento";
import { PRODUTOS_BLACKFEO, CATALOGOS_BLACKFEO, urlCheckoutBlackfeo } from "./catalogo";

export const BN26 = { inicio: "2026-11-04", fim: "2026-11-30", inicioIso: "2026-11-04T03:00:00Z", fimExclusivoIso: "2026-12-01T03:00:00Z", cupom: "BLACKFEO", metaTotal: 800_000 } as const;
export function faseBlackfeo(now = new Date()) {
  if (now.getTime() < Date.parse(BN26.inicioIso)) return "preparacao";
  return now.getTime() < Date.parse(BN26.fimExclusivoIso) ? "periodo-vendas" : "encerrada";
}

export type Linha = "posA" | "posB" | "cert" | "star" | "agente";
export type LinhaMeta = "pos" | "dobroCrm" | "outros";
export const LINHA_LABEL: Record<Linha | LinhaMeta, string> = {
  pos: "Pós individuais · catálogo",
  dobroCrm: "Black em Dobro · somente CRM",
  outros: "Outros produtos · certificações, combo, STAR e Agente",
  posA: "Pós série A (Orto, Dor, Esporte, Geronto)",
  posB: "Pós série B (Trauma, Prescrição)",
  cert: "Certificações (avulsa + combo)",
  star: "Star (1 ano, 3 anos, vitalícia)",
  agente: "Agente IA",
};

export const PRECOS = [
  { produto: "Pós série A (Orto, Dor, Esporte, Geronto)", de: 4997, por: "R$ 1.799 ou até 12x R$ 149 sem juros · 64% OFF · Assinatura 15x R$ 179 = R$ 2.685", bump: null },
  { produto: "Pós série B (Trauma, Prescrição)", de: 2697, por: "R$ 1.188 ou até 12x R$ 99 sem juros · 56% OFF · Assinatura 12x R$ 149 = R$ 1.788", bump: null },
  { produto: "Certificação avulsa (7 opções)", de: 697, por: "R$ 177 ou até 12x R$ 18,31 · 75% OFF", bump: 97 },
  { produto: "Combo 7 certificações · oferta extra", de: null, por: "R$ 497 ou até 12x R$ 51,40 · direto ao checkout", bump: 397 },
  { produto: "STAR 1 ano", de: 307, por: "R$ 97 ou até 12x R$ 10,03", bump: null },
  { produto: "STAR 3 anos", de: 497, por: "R$ 207 ou até 12x R$ 21,41", bump: null },
  { produto: "STAR vitalícia", de: 597, por: "R$ 247 ou até 12x R$ 25,55", bump: 147 },
  { produto: "Agente IA", de: 497, por: "R$ 97 ou até 12x R$ 10,03 · 80% OFF", bump: 47 },
] as const;

export const ORDER_BUMPS: { produto: string; bumps: string[] }[] = [
  { produto: "Certificação avulsa", bumps: ["Outras certificações"] },
  { produto: "Star vitalícia", bumps: ["Agente IA", "Combo certificações", "CBFEO"] },
  { produto: "Agente IA", bumps: ["E-book", "CBFEO"] },
  { produto: "Combo certificações", bumps: ["Star vitalícia", "CBFEO", "Agente IA"] },
  { produto: "Pós e Star 1/3 anos", bumps: ["A definir"] },
];
export const MAX_BUMPS = 5;

// Ticket líquido da pós: única em 12x desconta os juros absorvidos; assinatura conta o contrato inteiro.
export function ticketPos(serie: "A" | "B", pctUnica = 0.7) {
  const unica = serie === "A" ? valorLiquidoUnica(1799, 12) : valorLiquidoUnica(1188, 12);
  const assin = serie === "A" ? 179 * 15 : 149 * 12;
  return Math.round(pctUnica * unica + (1 - pctUnica) * assin);
}
export function ticketPosGeral(pctA = 0.8, pctUnica = 0.7) {
  return Math.round(pctA * ticketPos("A", pctUnica) + (1 - pctA) * ticketPos("B", pctUnica));
}

export type MetaLinha = { linha: LinhaMeta; vendas: number; ticket: number };
export function metasPadrao(): MetaLinha[] {
  return [
    { linha: "pos", vendas: 200, ticket: 1700 },
    { linha: "dobroCrm", vendas: 200, ticket: 1800 },
    { linha: "outros", vendas: 500, ticket: 200 },
  ];
}
/** Configurações antigas por produto não podem substituir as metas aprovadas por frente. */
export function normalizarMetas(salvas: unknown): MetaLinha[] {
  const padrao = metasPadrao();
  if (!Array.isArray(salvas) || !padrao.every((p) => salvas.some((s) => s?.linha === p.linha))) return padrao;
  return padrao.map((p) => {
    const s = salvas.find((s) => s?.linha === p.linha);
    return { linha: p.linha, vendas: Number.isFinite(s?.vendas) ? Math.max(0, Math.round(s.vendas)) : p.vendas, ticket: Number.isFinite(s?.ticket) ? Math.max(0, s.ticket) : p.ticket };
  });
}
export const faturamentoMeta = (m: MetaLinha[]) => m.reduce((s, x) => s + x.vendas * x.ticket, 0);

export function diasDaAcao(): string[] {
  const out: string[] = [];
  for (let d = new Date(BN26.inicio + "T12:00:00Z"); d.toISOString().slice(0, 10) <= BN26.fim; d.setUTCDate(d.getUTCDate() + 1)) out.push(d.toISOString().slice(0, 10));
  return out;
}

// Classifica uma venda em linha da ação pelo nome/categoria do produto.
export function linhaDaVenda(nome: string | null | undefined, categoria?: string | null): Linha | null {
  const n = (nome ?? "").toLowerCase();
  if (/dobro|black.*2.?x.?1|passaporte|gratuit|widget|tarefa para inserir/.test(n)) return null;
  if (/agente/.test(n)) return "agente";
  if (categoria === "star" || /\bstar\b/.test(n)) return "star";
  if (categoria === "certificacao" || /certifica/.test(n)) return "cert";
  if (categoria === "pos" || /p[óo]s/.test(n)) return /trauma|prescri/.test(n) ? "posB" : "posA";
  return null;
}

export type PaginaAcao = { id: string; nome: string; tipo: "catalogo" | "entregas" | "lp" | "checkout"; url: string; status: "a-produzir" | "implementado" | "no-ar"; produtoId?: string };
export function paginasPadrao(): PaginaAcao[] {
  return [
    ...CATALOGOS_BLACKFEO.map((p) => ({ ...p, tipo: (p.id === "catalogo-geral" ? "catalogo" : "entregas") as PaginaAcao["tipo"], status: "implementado" as const })),
    ...PRODUTOS_BLACKFEO.flatMap((p): PaginaAcao[] => [
      ...(p.lp ? [{ id: `lp-${p.id}`, produtoId: p.id, nome: p.nome, tipo: "lp" as const, url: p.lp, status: (p.linha === "posA" || p.linha === "posB" ? "a-produzir" : "implementado") as PaginaAcao["status"] }] : []),
      ...p.checkouts.map((c) => ({ id: `ck-${p.id}-${c.codigo}`, produtoId: p.id, nome: c.nome, tipo: "checkout" as const, url: urlCheckoutBlackfeo(c.codigo, c.nome === "Assinatura"), status: "no-ar" as const })),
    ]),
  ];
}

export type VendaBlackfeo = { product_id: string | null; product_name: string | null; checkout_code?: string | null; gross_value: number | null; revenue_value?: number | null; installments?: number | null; billing_type?: string | null; is_renewal?: boolean; raw_payload?: unknown; affiliate_id?: string | null; utm_source?: string | null; utm_medium?: string | null };
/** Mesmos afiliados comerciais registrados na operação Black; UTM CRM deve identificar uma vendedora. */
export function vendaDobroCrm(s: VendaBlackfeo): boolean {
  if (s.is_renewal || !["3115386", "3115431"].includes(s.product_id ?? "")) return false;
  return ["26015884", "91415218", "78373584"].includes(s.affiliate_id ?? "") ||
    ((s.utm_source ?? "").trim().toLowerCase() === "crm" && ["bruna", "daphne", "ingrid", "jana", "weruska", "patrícia", "patricia"].includes((s.utm_medium ?? "").trim().toLowerCase()));
}
export function faturamentoDobroCrm(s: VendaBlackfeo): number {
  if (!vendaDobroCrm(s)) return 0;
  const valor = Math.max(0, Number(s.gross_value ?? 0));
  if (s.product_id === "3115431") return Math.round(valor * 12 * 100) / 100;
  const raw = s.raw_payload as { data?: { installments?: number } } | null;
  return valorLiquidoUnica(valor, Math.max(1, Number(s.installments ?? raw?.data?.installments ?? 1)));
}
export function produtoDaVenda(s: VendaBlackfeo) {
  if (["3115386", "3115431", "2874137", "2874138"].includes(s.product_id ?? "")) return null;
  return PRODUTOS_BLACKFEO.find((p) => p.checkouts.some((c) => (c.productId && c.productId === s.product_id) || (s.checkout_code && c.codigo === s.checkout_code))) ?? null;
}
export function faturamentoVendaBlackfeo(s: VendaBlackfeo): number {
  if (s.is_renewal) return 0;
  const p = produtoDaVenda(s);
  if (!p) return 0;
  const valor = Math.max(0, Number(s.gross_value ?? 0));
  if (p.linha !== "posA" && p.linha !== "posB") return valor;
  const c = p.checkouts.find((c) => c.productId === s.product_id || c.codigo === s.checkout_code);
  if (c?.parcelasContrato) return Math.round(valor * c.parcelasContrato * 100) / 100;
  const raw = s.raw_payload as { data?: { installments?: number } } | null;
  return valorLiquidoUnica(valor, Math.max(1, Number(s.installments ?? raw?.data?.installments ?? 1)));
}
